import { useTranslation } from 'react-i18next';
import { Table, ScrollArea, Button, Group } from '@mantine/core';
import { formatCompactNumber, formatEnUsValue } from '../../utils/helpers';

function BillSummaryTable({ beforeMG, afterMG, showComparison = true }) {
  const { t } = useTranslation();
  const FIELDS = [
    { key: 'energy', label: t('result.billSummary.table.energy') },
    { key: 'demand', label: t('result.billSummary.table.demand') },
    { key: 'energyCharges', label: t('result.billSummary.table.energyCharges') },
    { key: 'energyRevenue', label: t('result.billSummary.table.energyRevenue') },
    { key: 'demandCharges', label: t('result.billSummary.table.demandCharges') },
    { key: 'fixedCharges', label: t('result.billSummary.table.fixedCharges') },
    { key: 'subscribedCharges', label: t('result.billSummary.table.subscribedCharges') },
    { key: 'penaltyCharges', label: t('result.billSummary.table.penaltyCharges') },
    { key: 'totalCharges', label: t('result.billSummary.table.totalCharges') },
  ];

  const primaryData = showComparison ? beforeMG : (afterMG || beforeMG);
  const months = primaryData?.map(bm => bm.periodStart) ?? [];

  const getFormattedCell = (value) => {
    if (value === null || value === undefined || value === '-') {
      return { display: '-', hover: '-' };
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return { display: value, hover: value };
    }
    return {
      display: formatCompactNumber(numericValue),
      hover: formatEnUsValue(numericValue),
    };
  };
  
  // Export CSV function
  const handleExportCSV = () => {
    // CSV-escape: wrap in quotes if value contains comma, quote, or non-ASCII
    const csvEscape = (val) => {
      const s = String(val);
      if (/[,"\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const headers = showComparison
      ? ['Date', ...FIELDS.flatMap(field => [`${field.label} (Before)`, `${field.label} (After)`])]
      : ['Date', ...FIELDS.map(field => field.label)];
    
    const rows = months.map((date, i) => [
      date,
      ...(showComparison
        ? FIELDS.flatMap(field => [
            beforeMG?.[i]?.[field.key] ?? '-',
            afterMG?.[i]?.[field.key] ?? '-',
          ])
        : FIELDS.map(field => primaryData?.[i]?.[field.key] ?? '-')),
    ]);

    // Create CSV content
    const csvContent = [
      headers.map(csvEscape).join(','),
      ...rows.map(row => row.map(cell => csvEscape(cell)).join(',')),
    ].join('\n');

    // Create blob and download with UTF-8 BOM for proper Unicode rendering (€, £, ¥, etc.)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bill_summary.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tableRows = months.map((date, i) => (
    <tr key={date}>
      <td>{date}</td>
      {showComparison
        ? FIELDS.map(field => (
            <>
              <td
                key={field.key + '-before'}
                style={{ fontWeight: 600 }}
                title={getFormattedCell(beforeMG?.[i]?.[field.key] ?? '-').hover}
              >
                {getFormattedCell(beforeMG?.[i]?.[field.key] ?? '-').display}
              </td>
              <td
                key={field.key + '-after'}
                style={{ fontWeight: 600 }}
                title={getFormattedCell(afterMG?.[i]?.[field.key] ?? '-').hover}
              >
                {getFormattedCell(afterMG?.[i]?.[field.key] ?? '-').display}
              </td>
            </>
          ))
        : FIELDS.map(field => (
            <td
              key={field.key}
              style={{ fontWeight: 600 }}
              title={getFormattedCell(primaryData?.[i]?.[field.key] ?? '-').hover}
            >
              {getFormattedCell(primaryData?.[i]?.[field.key] ?? '-').display}
            </td>
          ))}
    </tr>
  ));

  return (
    <div>
      <Group gap="xs" style={{ marginBottom: 16 }}>
        <Button
          size="xs"
          variant="default"
          onClick={handleExportCSV}
        >
          Export CSV
        </Button>
      </Group>
    <ScrollArea>
      <Table striped highlightOnHover withColumnBorders className='indicator-downloadable'>
        <thead>
          <tr>
            <th rowSpan={showComparison ? 2 : 1}>{t('common.date')}</th>
            {FIELDS.map(field => (
              <th
                key={field.key + '-component'}
                colSpan={showComparison ? 2 : 1}
                style={{ textAlign: 'center' }}
              >
                {field.label}
              </th>
            ))}
          </tr>
          {showComparison && (
            <tr>
              {FIELDS.map(field => (
                <>
                  <th key={field.key + '-before'} style={{ color: '#1976d2', textAlign: 'center' }}>{t('result.billSummary.legend.before')}</th>
                  <th key={field.key + '-after'} style={{ color: '#388e3c', textAlign: 'center' }}>{t('result.billSummary.legend.after')}</th>
                </>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {tableRows}
        </tbody>
      </Table>
    </ScrollArea>
   </div>
  );
}

export default BillSummaryTable;
